import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { TagState, BadgeType, GetTagsQuery, useGetBadgesQuery } from 'data/__generated__';

import { DetailFormArrayValueWithSelect } from 'components/DetailsView/DetailFormArrayValueWithSelect';
import { SectionColumn, SectionContainer, ColumnsSectionContainer } from 'components/DetailsView/Section';
import { Loader, LoaderModeType } from 'components/Loader';

import { WatchedArrayOfSelectFields } from '../types';

// Les badges ne sont chargés qu'à l'ouverture (`lazy`). Les trois listes sont des `useFieldArray` dont les
// valeurs sont dans les `defaultValues` du formulaire : ne pas monter la section ne change rien au save.
export const TagsAndBadgesSection = ({
    tags,
    isIncomingFromCreation = false,
}: {
    tags: GetTagsQuery['tags'];
    isIncomingFromCreation?: boolean;
}) => (
    <SectionContainer title="Tags et badges" isInitiallyOpen={isIncomingFromCreation} lazy>
        <TagsAndBadgesContent tags={tags} />
    </SectionContainer>
);

const TagsAndBadgesContent = ({ tags }: { tags: GetTagsQuery['tags'] }) => {
    const { control } = useFormContext();
    const selectedTags: WatchedArrayOfSelectFields = useWatch({
        control,
        name: 'tagOptions',
    });
    const selectedPreferences: WatchedArrayOfSelectFields = useWatch({
        control,
        name: 'preferences',
    });
    const selectedDiets: WatchedArrayOfSelectFields = useWatch({
        control,
        name: 'diets',
    });

    const { loading: loadingBadges, data: dataBadges, error: errorBadges } = useGetBadgesQuery();

    if (loadingBadges && !dataBadges) {
        return <Loader mode={LoaderModeType.Spin} />;
    }

    if (errorBadges || !dataBadges) {
        return <ErrorMessage>Erreur lors de la récupération des badges</ErrorMessage>;
    }

    const preferenceBadges = dataBadges.badges.filter((badge) => badge.type === BadgeType.Preference);
    const dietBadges = dataBadges.badges.filter((badge) => badge.type === BadgeType.Diet);

    const activeTags = tags.filter(({ state }) => state === TagState.Regular);

    return (
        <ColumnsSectionContainer numberOfColumns={3}>
            <SectionColumn>
                <DetailFormArrayValueWithSelect
                    name="tagOptions"
                    label="Tags"
                    options={activeTags.map(({ name, _id }) => {
                        return {
                            label: name,
                            value: _id,
                            isDisabled: selectedTags?.some(({ value }) => _id === value),
                        };
                    })}
                />
            </SectionColumn>
            <SectionColumn>
                <DetailFormArrayValueWithSelect
                    name="preferences"
                    label="Préferences"
                    options={preferenceBadges.map((badge) => {
                        return {
                            label: badge.name,
                            value: badge._id,
                            isDisabled: selectedPreferences?.some(({ value }) => badge._id === value),
                        };
                    })}
                />
            </SectionColumn>
            <SectionColumn>
                <DetailFormArrayValueWithSelect
                    name="diets"
                    label="Régimes"
                    options={dietBadges.map((badge) => {
                        return {
                            label: badge.name,
                            value: badge._id,
                            isDisabled: selectedDiets?.some(({ value }) => badge._id === value),
                        };
                    })}
                />
            </SectionColumn>
        </ColumnsSectionContainer>
    );
};

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.errorColor};
    padding: 10px;
`;
